import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, X, Plus, Trash2, Settings, Users, Compass, Bell, Send, Eye, Check, Link as LinkIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API, BACKEND_URL } from "../../lib/api";
import { ImageUploader } from "../ImageUploader";
import { ClientPicker } from "./ClientPicker";

export function AdminEventManager() {
  const [eventTypes, setEventTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeTab, setActiveTab] = useState('types');
  // Schedule form
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('19:00');
  const [maxParticipants, setMaxParticipants] = useState(10);
  const [eventSubtitle, setEventSubtitle] = useState('');
  const [creating, setCreating] = useState(false);
  // Edit-subtitle modal for existing event
  const [editEvtModal, setEditEvtModal] = useState(null); // { event_id, subtitle }
  const [regsModal, setRegsModal] = useState(null); // event being managed
  const [showCancelledRegs, setShowCancelledRegs] = useState(false);
  const [newReg, setNewReg] = useState({ name: '', email: '', phone: '', payment_method: 'pay_at_shop', selected_client: null });
  // New/Edit type form
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: '', description: '', short_description: '', duration_minutes: 120, price: 30, image_url: '', category: 'Atelier', min_participants: 2 });
  const [savingType, setSavingType] = useState(false);

  const refreshData = async () => {
    try {
      const [typesRes, eventsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/event-types`),
        axios.get(`${API}/events`),
        axios.get(`${API}/clients`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setEventTypes(typesRes.data);
      setEvents(eventsRes.data);
      setClients(clientsRes.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refreshData(); }, []);

  const handleCreateEvent = async () => {
    if (!selectedTypeId || !eventDate) { toast.error("Sélectionnez un type et une date"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/events`, {
        event_type_id: selectedTypeId,
        date: eventDate,
        time: eventTime,
        max_participants: maxParticipants,
        subtitle: eventSubtitle.trim() || null,
      }, { withCredentials: true });
      toast.success("Événement programmé !");
      setEventDate('');
      setEventSubtitle('');
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setCreating(false); }
  };

  const handleSaveSubtitle = async () => {
    if (!editEvtModal) return;
    try {
      await axios.put(`${API}/events/${editEvtModal.event_id}`, {
        subtitle: editEvtModal.subtitle || '',
      }, { withCredentials: true });
      toast.success("Sous-titre mis à jour");
      setEditEvtModal(null);
      refreshData();
    } catch (error) { toast.error("Erreur de mise à jour"); }
  };

  const refreshRegsModal = async () => {
    if (!regsModal?.event_id) return;
    try {
      const res = await axios.get(`${API}/events?upcoming_only=false`);
      const updated = (res.data || []).find(e => e.event_id === regsModal.event_id);
      if (updated) setRegsModal(updated);
      refreshData();
    } catch (e) { console.error(e); }
  };

  const addManualReg = async () => {
    if (!newReg.name.trim()) { toast.error("Nom requis"); return; }
    if (newReg.payment_method === 'online' && !newReg.email.trim() && !newReg.selected_client?.email) {
      toast.error("Un email est requis pour envoyer le lien de paiement en ligne"); return;
    }
    try {
      await axios.post(`${API}/events/${regsModal.event_id}/admin-register`, {
        name: newReg.name.trim(),
        email: newReg.email.trim() || null,
        phone: newReg.phone.trim() || null,
        user_id: newReg.selected_client?.user_id || null,
        payment_method: newReg.payment_method,
      }, { withCredentials: true });
      const labels = { paid: 'déjà réglé', pay_at_shop: 'paiement en boutique', online: 'lien de paiement envoyé par email' };
      toast.success(`${newReg.name} inscrit (${labels[newReg.payment_method]}) !`);
      setNewReg({ name: '', email: '', phone: '', payment_method: 'pay_at_shop', selected_client: null });
      refreshRegsModal();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const toggleRegAttended = async (reg) => {
    const regId = reg.registration_id || reg.user_id;
    try {
      await axios.put(`${API}/events/${regsModal.event_id}/registrations/${regId}`, { attended: !reg.attended }, { withCredentials: true });
      refreshRegsModal();
    } catch (e) { toast.error("Erreur"); }
  };

  const toggleRegPaid = async (reg) => {
    const regId = reg.registration_id || reg.user_id;
    try {
      await axios.put(`${API}/events/${regsModal.event_id}/registrations/${regId}`, { paid: !reg.paid }, { withCredentials: true });
      refreshRegsModal();
    } catch (e) { toast.error("Erreur"); }
  };

  const deleteReg = async (reg) => {
    if (!window.confirm(`Retirer ${reg.name || reg.email} ?`)) return;
    const regId = reg.registration_id || reg.user_id;
    try {
      await axios.delete(`${API}/events/${regsModal.event_id}/registrations/${regId}`, { withCredentials: true });
      toast.success("Inscription retirée");
      refreshRegsModal();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success("Événement supprimé");
      setEvents(prev => prev.filter(e => e.event_id !== eventId));
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const openNewType = () => {
    setEditingType('new');
    setTypeForm({ name: '', description: '', short_description: '', duration_minutes: 120, price: 30, image_url: '', category: 'Atelier', min_participants: 2 });
  };

  const openEditType = (t) => {
    setEditingType(t.event_type_id);
    setTypeForm({ name: t.name, description: t.description, short_description: t.short_description, duration_minutes: t.duration_minutes, price: t.price, image_url: t.image_url || '', category: t.category, min_participants: t.min_participants });
  };

  const handleSaveType = async () => {
    if (!typeForm.name || !typeForm.description) { toast.error("Nom et description requis"); return; }
    setSavingType(true);
    try {
      if (editingType === 'new') {
        await axios.post(`${API}/event-types`, typeForm, { withCredentials: true });
        toast.success("Type d'événement créé !");
      } else {
        await axios.put(`${API}/event-types/${editingType}`, typeForm, { withCredentials: true });
        toast.success("Type d'événement mis à jour !");
      }
      setEditingType(null);
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setSavingType(false); }
  };

  const handleDeleteType = async (typeId) => {
    try {
      await axios.delete(`${API}/event-types/${typeId}`, { withCredentials: true });
      toast.success("Type supprimé");
      refreshData();
    } catch (error) { toast.error("Erreur"); }
  };

  const tabs = [
    { id: 'types', label: "Types d'événements" },
    { id: 'schedule', label: 'Programmer' },
    { id: 'planned', label: `Planifiés (${events.length})` }
  ];

  return (
    <Card className="bg-[#261B3B] border-white/10 mt-8" data-testid="admin-event-manager">
      <CardHeader>
        <CardTitle className="font-serif text-xl flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#D926A9]" />
          Gestion des Événements
        </CardTitle>
        <div className="flex gap-2 mt-3">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-[#D926A9] text-white' : 'bg-[#1B132B] text-[#B59ED1] hover:text-white'}`}
              data-testid={`tab-${tab.id}`}
            >{tab.label}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {/* TAB: Types d'événements */}
        {activeTab === 'types' && (
          <div>
            {editingType ? (
              <div className="p-5 bg-[#1B132B] rounded-lg space-y-4" data-testid="type-form">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm uppercase tracking-wider text-[#B59ED1]">{editingType === 'new' ? 'Nouveau type' : 'Modifier'}</h3>
                  <button onClick={() => setEditingType(null)} className="text-[#B59ED1] hover:text-white text-sm">Annuler</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Nom</label>
                    <Input value={typeForm.name} onChange={(e) => setTypeForm({...typeForm, name: e.target.value})} className="bg-[#261B3B] border-white/10" data-testid="type-name" />
                  </div>
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Catégorie</label>
                    <Select value={typeForm.category} onValueChange={(v) => setTypeForm({...typeForm, category: v})}>
                      <SelectTrigger className="bg-[#261B3B] border-white/10" data-testid="type-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#261B3B] border-white/10">
                        <SelectItem value="Atelier" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Atelier</SelectItem>
                        <SelectItem value="Soirée" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Soirée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#B59ED1] mb-1 block">Accroche courte</label>
                  <Input value={typeForm.short_description} onChange={(e) => setTypeForm({...typeForm, short_description: e.target.value})} className="bg-[#261B3B] border-white/10" data-testid="type-short-desc" />
                </div>
                <div>
                  <label className="text-xs text-[#B59ED1] mb-1 block">Description complète</label>
                  <Textarea value={typeForm.description} onChange={(e) => setTypeForm({...typeForm, description: e.target.value})} className="bg-[#261B3B] border-white/10 min-h-[150px]" data-testid="type-description" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Prix (€)</label>
                    <Input type="number" value={typeForm.price} onChange={(e) => setTypeForm({...typeForm, price: parseFloat(e.target.value) || 0})} className="bg-[#261B3B] border-white/10" data-testid="type-price" />
                  </div>
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Durée (min)</label>
                    <Input type="number" value={typeForm.duration_minutes} onChange={(e) => setTypeForm({...typeForm, duration_minutes: parseInt(e.target.value) || 0})} className="bg-[#261B3B] border-white/10" data-testid="type-duration" />
                  </div>
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Min. participants</label>
                    <Input type="number" value={typeForm.min_participants} onChange={(e) => setTypeForm({...typeForm, min_participants: parseInt(e.target.value) || 1})} className="bg-[#261B3B] border-white/10" data-testid="type-min-part" />
                  </div>
                  <div>
                    <label className="text-xs text-[#B59ED1] mb-1 block">Photo</label>
                    <ImageUploader currentUrl={typeForm.image_url} onUploaded={(url) => setTypeForm({...typeForm, image_url: url})} category="events" />
                  </div>
                </div>
                <Button onClick={handleSaveType} disabled={savingType} className="gradient-primary" data-testid="save-type-btn">
                  {savingType ? 'Enregistrement...' : editingType === 'new' ? 'Créer le type' : 'Enregistrer les modifications'}
                </Button>
              </div>
            ) : (
              <>
                <Button onClick={openNewType} className="gradient-primary mb-4" data-testid="new-type-btn">
                  <Plus className="w-4 h-4 mr-2" />Nouveau type d'événement
                </Button>
                <div className="space-y-3">
                  {eventTypes.map(t => (
                    <div key={t.event_type_id} className="p-4 bg-[#1B132B] rounded-lg flex items-start gap-4" data-testid={`type-row-${t.event_type_id}`}>
                      {t.image_url && (
                        <img src={t.image_url} alt={t.name} className="w-20 h-14 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{t.name}</h4>
                          <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs flex-shrink-0">{t.category}</Badge>
                        </div>
                        <p className="text-[#B59ED1] text-sm truncate">{t.short_description}</p>
                        <div className="flex gap-3 mt-1 text-xs text-[#B59ED1]">
                          <span>{t.price}€</span>
                          <span>{t.duration_minutes} min</span>
                          <span>Min. {t.min_participants} pers.</span>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {t.slug && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#D926A9] hover:text-[#D926A9]/80"
                            onClick={() => {
                              const url = `${window.location.origin}/evenements/${t.slug}`;
                              navigator.clipboard.writeText(url).then(
                                () => toast.success(`Lien copié : /evenements/${t.slug}`),
                                () => toast.error("Impossible de copier")
                              );
                            }}
                            data-testid={`copy-event-link-${t.event_type_id}`}
                            title={`Copier le lien public — /evenements/${t.slug}`}
                          >
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-[#00E5FF] hover:text-[#00E5FF]/80" onClick={() => openEditType(t)} data-testid={`edit-type-${t.event_type_id}`}>
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteType(t.event_type_id)} data-testid={`delete-type-${t.event_type_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB: Programmer */}
        {activeTab === 'schedule' && (
          <div className="p-4 bg-[#1B132B] rounded-lg">
            <h3 className="text-sm uppercase tracking-wider text-[#B59ED1] mb-4">Programmer un événement</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Type d'événement</label>
                <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                  <SelectTrigger className="bg-[#261B3B] border-white/10" data-testid="schedule-type-select">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    {eventTypes.map(t => (
                      <SelectItem key={t.event_type_id} value={t.event_type_id} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">
                        {t.name} — {t.price}€
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Date</label>
                <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="bg-[#261B3B] border-white/10" data-testid="schedule-date" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Heure</label>
                <Input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} className="bg-[#261B3B] border-white/10" data-testid="schedule-time" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Places max</label>
                <Input type="number" value={maxParticipants} onChange={(e) => setMaxParticipants(parseInt(e.target.value) || 1)} min={1} className="bg-[#261B3B] border-white/10" data-testid="schedule-max" />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-[#B59ED1] mb-1 block">Sous-titre (optionnel)</label>
              <Input
                value={eventSubtitle}
                onChange={(e) => setEventSubtitle(e.target.value)}
                placeholder="ex. Pleine lune en Poisson — Nouvelle lune en Capricorne"
                className="bg-[#261B3B] border-white/10"
                maxLength={80}
                data-testid="schedule-subtitle"
              />
              <p className="text-[10px] text-[#B59ED1]/70 mt-1">Précise par exemple le signe astrologique de la lune. Affiché sous le titre de l'événement.</p>
            </div>
            <Button onClick={handleCreateEvent} disabled={creating} className="gradient-primary w-full" data-testid="schedule-create-btn">
              {creating ? 'Création...' : 'Programmer cet événement'}
            </Button>
          </div>
        )}

        {/* TAB: Événements planifiés */}
        {activeTab === 'planned' && (
          <div className="space-y-3">
            {events.length === 0 ? (
              <p className="text-[#B59ED1] text-center py-8">Aucun événement programmé</p>
            ) : events.map(evt => {
              const typeName = evt.event_type?.name || 'Événement';
              const dateObj = new Date(evt.date + 'T00:00:00');
              const dateStr = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
              const allRegs = evt.registrations || [];
              // On compte uniquement les inscriptions actives (non annulées et non en attente de paiement abandonnée)
              const activeRegs = allRegs.filter(r => r.status !== 'cancelled');
              const paidRegs = activeRegs.filter(r => r.paid || r.status === 'confirmed').length;
              const pendingRegs = activeRegs.filter(r => r.status === 'pending_payment' && !r.paid).length;
              const regs = activeRegs.length;
              return (
                <div key={evt.event_id} className="p-4 bg-[#1B132B] rounded-lg" data-testid={`admin-event-${evt.event_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">{typeName}</h4>
                      {evt.subtitle && (
                        <p className="text-[#D926A9] text-xs italic mb-0.5">{evt.subtitle}</p>
                      )}
                      <p className="text-[#B59ED1] text-sm capitalize">{dateStr} à {evt.time}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={regs >= evt.max_participants ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'} title={pendingRegs ? `${paidRegs} payé·e·s + ${pendingRegs} en attente paiement` : `${paidRegs} payé·e·s`}>
                        {regs}/{evt.max_participants} inscrit(s){pendingRegs ? ` · ${pendingRegs}⏳` : ''}
                      </Badge>
                      {evt.event_type?.slug && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#D926A9] hover:text-[#D926A9]/80"
                          onClick={() => {
                            const url = `${window.location.origin}/evenements/${evt.event_type.slug}`;
                            navigator.clipboard.writeText(url).then(
                              () => toast.success(`Lien copié : /evenements/${evt.event_type.slug}`),
                              () => toast.error("Impossible de copier")
                            );
                          }}
                          data-testid={`copy-event-link-${evt.event_id}`}
                          title={`Copier le lien public — /evenements/${evt.event_type.slug}`}
                        >
                          <LinkIcon className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="text-[#9D4CDD] hover:text-[#9D4CDD]/80" onClick={() => setRegsModal(evt)} data-testid={`view-regs-${evt.event_id}`} title="Gérer les inscrits">
                        <Users className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-[#00E5FF] hover:text-[#00E5FF]/80" onClick={() => setEditEvtModal({ event_id: evt.event_id, subtitle: evt.subtitle || '' })} data-testid={`edit-event-${evt.event_id}`} title="Modifier sous-titre">
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteEvent(evt.event_id)} data-testid={`delete-event-${evt.event_id}`} title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {regs > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/5">
                      <p className="text-xs text-[#B59ED1] mb-1">Inscrits :</p>
                      <div className="flex flex-wrap gap-2">
                        {evt.registrations.map((r, i) => (
                          <Badge key={r.user_id || r.email || r.name || i} className="bg-[#261B3B] text-[#E2D5F8] text-xs">{r.name || r.email}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Modal édition sous-titre événement */}
      <AnimatePresence>
        {editEvtModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditEvtModal(null)} />
            <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-md w-full" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="edit-event-subtitle-modal">
              <h3 className="font-serif text-2xl mb-4">Modifier le sous-titre</h3>
              <Input
                value={editEvtModal.subtitle}
                onChange={(e) => setEditEvtModal({...editEvtModal, subtitle: e.target.value})}
                placeholder="ex. Pleine lune en Poisson"
                className="bg-[#1B132B] border-white/10 mb-4"
                maxLength={80}
                autoFocus
                data-testid="edit-subtitle-input"
              />
              <p className="text-[10px] text-[#B59ED1] mb-4">Laisse vide pour supprimer le sous-titre.</p>
              <div className="flex gap-2">
                <Button onClick={handleSaveSubtitle} className="gradient-primary flex-1" data-testid="save-subtitle-btn">Enregistrer</Button>
                <Button variant="outline" onClick={() => setEditEvtModal(null)} className="border-white/10">Annuler</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal gestion inscrits */}
      <AnimatePresence>
        {regsModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setRegsModal(null); setShowCancelledRegs(false); }} />
            <motion.div className="relative bg-[#261B3B] rounded-2xl border border-white/15 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" initial={{scale:0.9}} animate={{scale:1}} exit={{scale:0.9}} data-testid="event-regs-modal">
              {(() => {
                const allRegs = regsModal.registrations || [];
                const activeRegs = allRegs.filter(r => r.status !== 'cancelled');
                const cancelledRegs = allRegs.filter(r => r.status === 'cancelled');
                const visibleRegs = showCancelledRegs ? allRegs : activeRegs;
                return (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-serif text-2xl flex items-center gap-2"><Users className="w-5 h-5 text-[#9D4CDD]" />Inscrits</h3>
                        <p className="text-sm text-[#B59ED1] mt-1">{regsModal.date} à {regsModal.time} {regsModal.subtitle && <span className="italic text-[#D926A9]">— {regsModal.subtitle}</span>}</p>
                        <p className="text-xs text-[#B59ED1] mt-0.5">{activeRegs.length}/{regsModal.max_participants} places{cancelledRegs.length ? ` · ${cancelledRegs.length} annulé(s)` : ''}</p>
                      </div>
                      <button onClick={() => { setRegsModal(null); setShowCancelledRegs(false); }} className="text-[#B59ED1] hover:text-white"><X className="w-5 h-5" /></button>
                    </div>

                    {cancelledRegs.length > 0 && (
                      <button
                        onClick={() => setShowCancelledRegs(v => !v)}
                        className="text-xs text-[#B59ED1] hover:text-[#D926A9] mb-3 underline-offset-2 hover:underline"
                        data-testid="toggle-cancelled-regs"
                      >
                        {showCancelledRegs ? "Masquer les inscriptions annulées" : `Voir les inscriptions annulées (${cancelledRegs.length})`}
                      </button>
                    )}

                    {/* Liste des inscrits */}
                    {visibleRegs.length === 0 ? (
                      <p className="text-center text-[#B59ED1] text-sm py-6">Aucun inscrit pour le moment</p>
                    ) : (
                      <div className="space-y-2 mb-5">
                        {visibleRegs.map((r, i) => {
                          const isPending = r.status === 'pending_payment' && !r.paid;
                          const isCancelled = r.status === 'cancelled';
                          const badgeClass = r.paid
                            ? 'border-green-500/40 bg-green-500/15 text-green-400'
                            : isPending
                              ? 'border-orange-500/40 bg-orange-500/15 text-orange-400'
                              : 'border-red-500/40 bg-red-500/15 text-red-400';
                          const badgeLabel = r.paid
                            ? '✓ Payé'
                            : isPending
                              ? '⏳ En attente paiement'
                              : '✗ Non payé';
                  return (
                    <div key={r.registration_id || r.user_id || i} className={`p-3 bg-[#1B132B] rounded-lg flex items-center gap-3 ${isCancelled ? 'opacity-40' : ''}`} data-testid={`reg-row-${r.registration_id || r.user_id || i}`}>
                      <input type="checkbox" checked={!!r.attended} onChange={() => toggleRegAttended(r)} className="w-5 h-5 rounded accent-[#00E5FF] cursor-pointer flex-shrink-0" data-testid={`reg-attended-${i}`} title={r.attended ? "Présent·e" : "Cocher si présent·e"} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#F7F3FC]">
                          {r.name || r.email || '—'}
                          {r.manual && <span className="text-[10px] text-[#B59ED1] ml-1">(ajout manuel)</span>}
                          {isCancelled && <span className="text-[10px] text-red-400 ml-1">(annulé)</span>}
                        </p>
                        <p className="text-xs text-[#B59ED1] truncate">{[r.email, r.phone].filter(Boolean).join(' · ') || '—'}</p>
                      </div>
                      <button onClick={() => toggleRegPaid(r)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${badgeClass}`} data-testid={`reg-paid-${i}`} title="Cliquer pour basculer">
                        {badgeLabel} {r.amount_paid != null && r.amount_paid > 0 && <span className="ml-1">({r.amount_paid}€)</span>}
                      </button>
                      <Button variant="ghost" size="sm" className="text-red-400" onClick={() => deleteReg(r)} data-testid={`reg-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  );
                  })}
                </div>
              )}

              {/* Formulaire ajout manuel — avec recherche dans la base + création client */}
              <div className="p-4 bg-[#1B132B] rounded-lg space-y-3 border border-[#D926A9]/20">
                <h4 className="text-sm font-medium text-[#D926A9] flex items-center gap-2"><Plus className="w-4 h-4" />Ajouter manuellement un inscrit</h4>

                <ClientPicker
                  clients={clients}
                  selected={newReg.selected_client}
                  onSelect={(c) => {
                    if (c) {
                      setNewReg({ ...newReg, selected_client: c, name: c.name || '', email: c.email || '', phone: c.phone || '' });
                    } else {
                      setNewReg({ ...newReg, selected_client: null, name: '', email: '', phone: '' });
                    }
                  }}
                  onCreated={(c) => setClients(prev => [c, ...prev.filter(x => x.user_id !== c.user_id)])}
                  placeholder="Rechercher un client par nom, email ou téléphone…"
                  dataTestidPrefix="reg-add-client"
                />

                {/* Édition manuelle (sans compte client) */}
                {!newReg.selected_client && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input value={newReg.name} onChange={(e) => setNewReg({...newReg, name: e.target.value})} placeholder="Nom *" className="bg-[#261B3B] border-white/10" data-testid="reg-add-name" />
                    <Input value={newReg.email} onChange={(e) => setNewReg({...newReg, email: e.target.value})} placeholder="Email (optionnel)" type="email" className="bg-[#261B3B] border-white/10" data-testid="reg-add-email" />
                    <Input value={newReg.phone} onChange={(e) => setNewReg({...newReg, phone: e.target.value})} placeholder="Téléphone (optionnel)" className="bg-[#261B3B] border-white/10" data-testid="reg-add-phone" />
                  </div>
                )}

                <div>
                  <label className="text-xs text-[#B59ED1] mb-1 block">Mode de règlement</label>
                  <select
                    value={newReg.payment_method}
                    onChange={(e) => setNewReg({...newReg, payment_method: e.target.value})}
                    className="w-full bg-[#261B3B] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="reg-add-payment-method"
                  >
                    <option value="pay_at_shop">À régler en boutique le jour J</option>
                    <option value="paid">Déjà réglé (espèces, virement, etc.)</option>
                    <option value="online">À régler en ligne (envoie un email avec lien)</option>
                  </select>
                  {newReg.payment_method === 'online' && (
                    <p className="text-[10px] text-[#B59ED1]/80 mt-1.5">
                      💡 Un email avec lien de paiement sera envoyé au client. La place est réservée mais l'inscription ne sera définitivement confirmée qu'après paiement.
                    </p>
                  )}
                </div>
                <Button onClick={addManualReg} className="gradient-primary w-full" data-testid="reg-add-submit"><Plus className="w-4 h-4 mr-1" />Ajouter à l'événement</Button>
              </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
